import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Image as ImageIcon, Newspaper, RefreshCw, Megaphone, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';
import { useEventsModule } from '../../../context/EventsModuleContext';

const DashboardView = () => {
  const { eventsEnabled } = useEventsModule();
  const [stats, setStats] = useState({
    heroes: 0,
    news: 0,
    photos: 0,
  });
  const [rssSettings, setRssSettings] = useState({
    auto_fetch_enabled: false,
    fetch_interval_hours: 6,
    last_fetch: null,
    feeds: []
  });
  const [calendarSettings, setCalendarSettings] = useState({
    auto_fetch_enabled: false,
    fetch_interval_hours: 6,
    weeks_ahead: 2,
    last_fetch: null,
    churches: []
  });
  const [savingRss, setSavingRss] = useState(false);
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [fetchingCalendars, setFetchingCalendars] = useState(false);
  const [syncingChurch, setSyncingChurch] = useState(null);
  const [clearingCalendars, setClearingCalendars] = useState(false); // Track which church is syncing

  useEffect(() => {
    loadStats();
    loadRssSettings();
    loadCalendarSettings();
  }, []);

  const loadStats = async () => {
    try {
      const [heroes, news, photos] = await Promise.all([
        api.getHeroes(),
        api.getNews(100),
        api.getPhotos(),
      ]);
      setStats({
        heroes: heroes.length,
        news: news.length,
        photos: photos.length,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadRssSettings = async () => {
    try {
      const settings = await api.getRssSettings();
      setRssSettings(settings);
    } catch (error) {
      console.error('Error loading RSS settings:', error);
    }
  };

  const loadCalendarSettings = async () => {
    try {
      const settings = await api.getCalendarSettings();
      setCalendarSettings(settings);
    } catch (error) {
      console.error('Error loading calendar settings:', error);
    }
  };

  const handleUpdateRSS = async () => {
    try {
      const result = await api.updateRSSFeeds();
      toast.success(result.message);
      loadStats();
      loadRssSettings();
    } catch (error) {
      toast.error('Kunde inte uppdatera RSS-flöden');
    }
  };

  const handleSaveRssSettings = async () => {
    setSavingRss(true);
    try {
      await api.updateRssSettings(rssSettings.auto_fetch_enabled, rssSettings.fetch_interval_hours);
      toast.success('RSS-inställningar sparade');
    } catch (error) {
      toast.error('Kunde inte spara RSS-inställningar');
    } finally {
      setSavingRss(false);
    }
  };

  const handleFetchCalendars = async () => {
    setFetchingCalendars(true);
    try {
      const result = await api.fetchCalendars();
      toast.success(result.message);
      loadCalendarSettings();
    } catch (error) {
      // Show more specific error message
      if (error.code === 'ECONNABORTED') {
        toast.error('Tidsgräns överskriden - hämtning kan fortfarande pågå i bakgrunden');
      } else if (error.response?.status === 401) {
        toast.error('Sessionen har gått ut - logga in igen');
      } else {
        toast.error('Kunde inte hämta kalender - kontrollera CalDAV-inställningar');
      }
    } finally {
      setFetchingCalendars(false);
    }
  };

  const handleSyncChurch = async (churchId, clearFirst = false, type = null) => {
    setSyncingChurch(`${churchId}:${type || 'all'}`);
    try {
      const result = await api.fetchSingleCalendar(churchId, clearFirst, type);
      if (result.events_skipped) {
        toast.info('Händelser-modulen är avaktiverad — bara mässor synkades.');
      } else if (result.success) {
        toast.success(result.message);
      } else {
        toast.warning(result.message);
      }
      if (result.errors && result.errors.length > 0) {
        result.errors.forEach(err => toast.error(err));
      }
      loadCalendarSettings();
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error(`Tidsgräns överskriden för ${churchId}`);
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(`Kunde inte synka ${churchId}`);
      }
    } finally {
      setSyncingChurch(null);
    }
  };

  const handleClearAllCalendars = async () => {
    if (!window.confirm('Är du säker på att du vill ta bort ALLA kalender-händelser? Detta kan inte ångras.')) {
      return;
    }
    setClearingCalendars(true);
    try {
      const result = await api.clearAllCalendars();
      toast.success(result.message);
      loadCalendarSettings();
    } catch (error) {
      toast.error('Kunde inte rensa kalender-händelser');
    } finally {
      setClearingCalendars(false);
    }
  };

  const handleClearChurch = async (churchId) => {
    if (!window.confirm(`Är du säker på att du vill ta bort alla händelser för ${churchId}?`)) {
      return;
    }
    setSyncingChurch(churchId);
    try {
      const result = await api.clearChurchCalendar(churchId);
      toast.success(result.message);
      loadCalendarSettings();
    } catch (error) {
      toast.error(`Kunde inte rensa händelser för ${churchId}`);
    } finally {
      setSyncingChurch(null);
    }
  };

  const handleSaveCalendarSettings = async () => {
    setSavingCalendar(true);
    try {
      await api.updateCalendarSettings({
        churches: calendarSettings.churches,
        weeks_ahead: calendarSettings.weeks_ahead,
        fetch_interval_hours: calendarSettings.fetch_interval_hours,
        auto_fetch_enabled: calendarSettings.auto_fetch_enabled
      });
      toast.success('Kalender-inställningar sparade');
      loadCalendarSettings();
    } catch (error) {
      toast.error('Kunde inte spara kalender-inställningar');
    } finally {
      setSavingCalendar(false);
    }
  };

  const handleUpdateDailyReading = async () => {
    try {
      const result = await api.updateDailyReadings();
      toast.success(result.message);
    } catch (error) {
      toast.error('Kunde inte uppdatera dagens läsning');
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-view">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Översikt över ditt innehåll</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Heroes</p>
                <p className="text-3xl font-bold text-gray-900">{stats.heroes}</p>
              </div>
              <ImageIcon className="w-12 h-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Nyheter</p>
                <p className="text-3xl font-bold text-gray-900">{stats.news}</p>
              </div>
              <Newspaper className="w-12 h-12 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Foton</p>
                <p className="text-3xl font-bold text-gray-900">{stats.photos}</p>
              </div>
              <ImageIcon className="w-12 h-12 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>RSS-flöden & Automatiska uppdateringar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-semibold text-gray-900">Hämta nyheter manuellt</h3>
              <p className="text-sm text-gray-600">Hämta senaste nyheter från Vatican News, Signum och Katolska Kyrkan</p>
            </div>
            <Button onClick={handleUpdateRSS} data-testid="update-rss-button">
              <RefreshCw className="w-4 h-4 mr-2" />
              Uppdatera nu
            </Button>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-4">Auto-hämtning inställningar</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="autoFetchEnabled"
                  checked={rssSettings.auto_fetch_enabled}
                  onChange={(e) => setRssSettings({...rssSettings, auto_fetch_enabled: e.target.checked})}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  data-testid="auto-fetch-checkbox"
                />
                <label htmlFor="autoFetchEnabled" className="text-sm font-medium text-gray-900">
                  Aktivera automatisk hämtning av nyheter
                </label>
              </div>
              
              <div className="flex items-center space-x-3">
                <label className="text-sm text-gray-700">Hämta var</label>
                <select
                  value={rssSettings.fetch_interval_hours}
                  onChange={(e) => setRssSettings({...rssSettings, fetch_interval_hours: parseInt(e.target.value)})}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                  data-testid="fetch-interval-select"
                >
                  <option value={1}>1 timme</option>
                  <option value={3}>3 timmar</option>
                  <option value={6}>6 timmar</option>
                  <option value={12}>12 timmar</option>
                  <option value={24}>24 timmar</option>
                </select>
              </div>
              
              <Button 
                onClick={handleSaveRssSettings} 
                disabled={savingRss}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-rss-settings-button"
              >
                {savingRss ? 'Sparar...' : 'Spara inställningar'}
              </Button>
              
              {rssSettings.last_fetch && (
                <p className="text-xs text-gray-500">
                  Senast hämtad: {new Date(rssSettings.last_fetch).toLocaleString('sv-SE')}
                </p>
              )}
            </div>
          </div>

          {rssSettings.feeds && rssSettings.feeds.length > 0 && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Aktiva RSS-källor</h3>
              <ul className="space-y-2">
                {rssSettings.feeds.map((feed, idx) => (
                  <li key={`feed-${feed.name || idx}`} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{feed.name}</span>
                    <span className="text-gray-500 text-xs">
                      {feed.category === 'vatican' ? '🌍 Vatikanen' : '⛪ Katolskt'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-semibold text-gray-900">Dagens bibelläsning</h3>
              <p className="text-sm text-gray-600">Lägg till dagens bibelläsningar</p>
            </div>
            <Button onClick={handleUpdateDailyReading} data-testid="update-reading-button">
              <RefreshCw className="w-4 h-4 mr-2" />
              Uppdatera
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Kalender-integration (iCal)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-semibold text-gray-900">Hämta kalender-händelser</h3>
              <p className="text-sm text-gray-600">Hämta mässor och kungörelser från kyrkans kalendrar</p>
            </div>
            <Button onClick={handleFetchCalendars} disabled={fetchingCalendars} data-testid="fetch-calendars-button">
              <RefreshCw className={`w-4 h-4 mr-2 ${fetchingCalendars ? 'animate-spin' : ''}`} />
              {fetchingCalendars ? 'Hämtar...' : 'Hämta nu'}
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
            <div>
              <h3 className="font-semibold text-gray-900">Rensa alla kalender-händelser</h3>
              <p className="text-sm text-gray-600">Ta bort alla händelser från databasen (kan inte ångras)</p>
            </div>
            <Button 
              onClick={handleClearAllCalendars} 
              disabled={clearingCalendars}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              data-testid="clear-all-calendars-button"
            >
              {clearingCalendars ? 'Rensar...' : 'Rensa alla händelser'}
            </Button>
          </div>

          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h3 className="font-semibold text-gray-900 mb-4">Kalender auto-hämtning</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="calendarAutoFetch"
                  checked={calendarSettings.auto_fetch_enabled}
                  onChange={(e) => setCalendarSettings({...calendarSettings, auto_fetch_enabled: e.target.checked})}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  data-testid="calendar-auto-fetch-checkbox"
                />
                <label htmlFor="calendarAutoFetch" className="text-sm font-medium text-gray-900">
                  Aktivera automatisk hämtning
                </label>
              </div>
              
              <div className="flex items-center space-x-3">
                <label className="text-sm text-gray-700">Hämta var</label>
                <select
                  value={calendarSettings.fetch_interval_hours}
                  onChange={(e) => setCalendarSettings({...calendarSettings, fetch_interval_hours: parseInt(e.target.value)})}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                  data-testid="calendar-interval-select"
                >
                  <option value={1}>1 timme</option>
                  <option value={3}>3 timmar</option>
                  <option value={6}>6 timmar</option>
                  <option value={12}>12 timmar</option>
                  <option value={24}>24 timmar</option>
                </select>
              </div>

              <div className="flex items-center space-x-3">
                <label className="text-sm text-gray-700">Veckor framåt</label>
                <select
                  value={calendarSettings.weeks_ahead}
                  onChange={(e) => setCalendarSettings({...calendarSettings, weeks_ahead: parseInt(e.target.value)})}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                  data-testid="weeks-ahead-select"
                >
                  <option value={1}>1 vecka</option>
                  <option value={2}>2 veckor</option>
                  <option value={3}>3 veckor</option>
                  <option value={4}>4 veckor</option>
                </select>
              </div>
              
              <Button 
                onClick={handleSaveCalendarSettings} 
                disabled={savingCalendar}
                className="bg-green-600 hover:bg-green-700"
                data-testid="save-calendar-settings-button"
              >
                {savingCalendar ? 'Sparar...' : 'Spara inställningar'}
              </Button>
              
              {calendarSettings.last_fetch && (
                <p className="text-xs text-gray-500">
                  Senast hämtad: {new Date(calendarSettings.last_fetch).toLocaleString('sv-SE')}
                </p>
              )}
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-1">Kalender-konfiguration per kyrka</h3>
            <p className="text-xs text-gray-600 mb-4">
              Stöder både <strong>CalDAV</strong> (med användarnamn/lösenord) och <strong>iCal-prenumeration</strong>
              {' '}(t.ex. Google Kalender publik eller hemlig iCal-adress). Källan upptäcks automatiskt från URL:en.
              Lämna användarnamn/lösenord tomma för publika iCal-adresser.
            </p>
            <div className="space-y-6">
              {calendarSettings.churches && calendarSettings.churches.map((church, idx) => (
                <div key={church.id} className="p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center flex-wrap gap-2">
                    <span>⛪ {church.name}</span>
                    {(church.masses_url || church.events_url) && (
                      <span
                        className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs"
                        data-testid={`configured-badge-${church.id}`}
                      >
                        Konfigurerad
                      </span>
                    )}
                    {church.detected_source === 'ical' && (
                      <span
                        className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs"
                        data-testid={`source-badge-${church.id}`}
                      >
                        iCal
                      </span>
                    )}
                    {church.detected_source === 'caldav' && (
                      <span
                        className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs"
                        data-testid={`source-badge-${church.id}`}
                      >
                        CalDAV
                      </span>
                    )}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Användarnamn <span className="text-gray-400">(endast CalDAV)</span>
                      </label>
                      <Input
                        type="text"
                        placeholder="user@fatimakrona.se (lämna tomt för iCal)"
                        value={church.username || ''}
                        onChange={(e) => {
                          const updated = [...calendarSettings.churches];
                          updated[idx] = {...updated[idx], username: e.target.value};
                          setCalendarSettings({...calendarSettings, churches: updated});
                        }}
                        className="text-sm"
                        data-testid={`calendar-username-${church.id}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Lösenord <span className="text-gray-400">(endast CalDAV)</span>
                      </label>
                      <Input
                        type="password"
                        placeholder="•••••••• (lämna tomt för iCal)"
                        value={church.password || ''}
                        onChange={(e) => {
                          const updated = [...calendarSettings.churches];
                          updated[idx] = {...updated[idx], password: e.target.value};
                          setCalendarSettings({...calendarSettings, churches: updated});
                        }}
                        className="text-sm"
                        data-testid={`calendar-password-${church.id}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Mässor – Kalender URL (CalDAV eller iCal)</label>
                      <Input
                        type="url"
                        placeholder="https://calendar.google.com/calendar/ical/.../basic.ics  •  eller  https://server/calendars/.../calendar-..."
                        value={church.masses_url || ''}
                        onChange={(e) => {
                          const updated = [...calendarSettings.churches];
                          updated[idx] = {...updated[idx], masses_url: e.target.value};
                          setCalendarSettings({...calendarSettings, churches: updated});
                        }}
                        className="text-sm"
                        data-testid={`calendar-masses-url-${church.id}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Händelser/Kungörelser – Kalender URL (CalDAV eller iCal)</label>
                      <Input
                        type="url"
                        placeholder="https://calendar.google.com/calendar/ical/.../basic.ics  •  eller  https://server/calendars/.../calendar-..."
                        value={church.events_url || ''}
                        onChange={(e) => {
                          const updated = [...calendarSettings.churches];
                          updated[idx] = {...updated[idx], events_url: e.target.value};
                          setCalendarSettings({...calendarSettings, churches: updated});
                        }}
                        className="text-sm"
                        data-testid={`calendar-events-url-${church.id}`}
                      />
                    </div>
                    <div className="md:col-span-2 pt-2 border-t border-gray-100">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Mass sync controls — always available */}
                        <div className="bg-blue-50/40 border border-blue-100 rounded-lg p-3 space-y-2">
                          <div className="flex items-center gap-2 text-blue-700 font-semibold text-xs">
                            <RefreshCw className="w-3 h-3" />
                            Mässor
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSyncChurch(church.id, false, 'mass')}
                              disabled={syncingChurch !== null || !church.masses_url}
                              className="flex-1 text-xs"
                              data-testid={`sync-${church.id}-mass-button`}
                            >
                              <RefreshCw className={`w-3 h-3 mr-1 ${syncingChurch === `${church.id}:mass` ? 'animate-spin' : ''}`} />
                              {syncingChurch === `${church.id}:mass` ? 'Synkar...' : 'Hämta mässor'}
                            </Button>
                          </div>
                        </div>

                        {/* Events sync controls — disabled when module off */}
                        <div className={`border rounded-lg p-3 space-y-2 ${eventsEnabled ? 'bg-amber-50/40 border-amber-100' : 'bg-gray-100 border-gray-200 opacity-70'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-amber-700 font-semibold text-xs">
                              <Megaphone className="w-3 h-3" />
                              Händelser
                            </div>
                            {!eventsEnabled && (
                              <span className="text-[10px] bg-gray-200 text-gray-600 rounded-full px-2 py-0.5 flex items-center gap-1">
                                <AlertCircle className="w-2.5 h-2.5" />
                                Modul AV
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSyncChurch(church.id, false, 'event')}
                              disabled={syncingChurch !== null || !church.events_url || !eventsEnabled}
                              className="flex-1 text-xs"
                              data-testid={`sync-${church.id}-event-button`}
                            >
                              <RefreshCw className={`w-3 h-3 mr-1 ${syncingChurch === `${church.id}:event` ? 'animate-spin' : ''}`} />
                              {syncingChurch === `${church.id}:event` ? 'Synkar...' : 'Hämta händelser'}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Combined / destructive actions */}
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSyncChurch(church.id, true)}
                          disabled={syncingChurch !== null || (!church.masses_url && !church.events_url)}
                          className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-50 text-xs"
                          data-testid={`clear-sync-${church.id}-button`}
                        >
                          <RefreshCw className={`w-3 h-3 mr-1 ${syncingChurch === `${church.id}:all` ? 'animate-spin' : ''}`} />
                          {syncingChurch === `${church.id}:all` ? 'Rensar...' : 'Rensa & Synka allt'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleClearChurch(church.id)}
                          disabled={syncingChurch !== null}
                          className="border-red-300 text-red-700 hover:bg-red-50 text-xs"
                          data-testid={`clear-${church.id}-button`}
                        >
                          Rensa
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Button 
              onClick={handleSaveCalendarSettings} 
              disabled={savingCalendar}
              className="mt-4 bg-green-600 hover:bg-green-700 w-full"
            >
              {savingCalendar ? 'Sparar...' : 'Spara alla kalender-inställningar'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardView;
